import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { useApp } from "../context/AppContext";
import customerApi from "../lib/customerApi";
import api from "../lib/api";
import { toast } from "sonner";
import {
  LogOut, FileText, Calendar, Plus, Minus, ShoppingBag, CheckCircle2, Clock, Truck, Package, XCircle,
  Download,
} from "lucide-react";

const STATUS_ICON = {
  pending: Clock,
  confirmed: CheckCircle2,
  packed: Package,
  out_for_delivery: Truck,
  delivered: CheckCircle2,
  cancelled: XCircle,
};
const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-blue-100 text-blue-800",
  packed: "bg-indigo-100 text-indigo-800",
  out_for_delivery: "bg-purple-100 text-purple-800",
  delivered: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};

export default function CustomerDashboard() {
  const { lang } = useApp();
  const { customer, logout, booted, getToken } = useCustomerAuth();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [reviewOrder, setReviewOrder] = useState(null);

  const reload = async () => {
    try {
      const [o, p] = await Promise.all([
        customerApi.get("/customers/me/orders"),
        api.get("/products"),
      ]);
      setOrders(o.data);
      setProducts(p.data);
    } catch {
      // silent
    }
  };

  useEffect(() => {
    if (customer) reload();
  }, [customer]);

  if (!booted) return <div className="p-10 text-stone-500">Loading...</div>;
  if (!customer) return <Navigate to="/customer/login" replace />;

  const downloadInvoice = async (orderId, code) => {
    try {
      const token = getToken();
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderId}/invoice.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${code}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Couldn't download invoice");
    }
  };

  return (
    <div data-testid="customer-dashboard" className="bg-[#fbf8f1] min-h-[90vh]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[11px] tracking-[0.2em] uppercase text-[#b45f06] font-semibold">
              {customer.business_type === "wholesale" ? "Wholesale account" : "Account"}
            </div>
            <h1 className="text-3xl font-semibold text-stone-900 mt-1">
              {customer.business_name}
            </h1>
            <p className="text-stone-500 text-sm mt-1">
              {customer.contact_name} · {customer.phone} · GSTIN: {customer.gstin}
            </p>
          </div>
          <button
            data-testid="cust-logout"
            onClick={logout}
            className="inline-flex items-center gap-2 px-4 py-2 border border-stone-300 rounded-sm text-sm hover:bg-white"
          >
            <LogOut size={14} /> {lang === "en" ? "Logout" : "లాగౌట్"}
          </button>
        </div>

        <div className="mt-8 border-b border-stone-200 flex gap-6">
          {[
            { key: "orders", label: lang === "en" ? "My Orders" : "నా ఆర్డర్లు", icon: FileText },
            { key: "prebook", label: lang === "en" ? "Pre-book Order" : "ముందస్తు బుకింగ్", icon: Calendar },
          ].map((tt) => (
            <button
              key={tt.key}
              data-testid={`cust-tab-${tt.key}`}
              onClick={() => setTab(tt.key)}
              className={`pb-3 text-sm font-semibold tracking-wide transition-colors inline-flex items-center gap-2 ${
                tab === tt.key ? "text-[#274e13] border-b-2 border-[#274e13]" : "text-stone-500"
              }`}
            >
              <tt.icon size={14} /> {tt.label}
            </button>
          ))}
        </div>

        {tab === "orders" && (
          <div className="mt-8">
            {orders.length === 0 ? (
              <div className="bg-white border border-stone-200 rounded-sm p-10 text-center">
                <ShoppingBag size={32} className="mx-auto text-stone-400" />
                <p className="mt-3 text-stone-600">{lang === "en" ? "No orders yet." : "ఇంకా ఆర్డర్లు లేవు."}</p>
                <button
                  onClick={() => setTab("prebook")}
                  className="mt-4 bg-[#274e13] hover:bg-[#38761d] text-white px-5 py-2 rounded-sm text-sm font-semibold"
                >
                  {lang === "en" ? "Place your first order" : "మీ మొదటి ఆర్డర్"}
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {orders.map((o) => {
                  const Icon = STATUS_ICON[o.status] || Clock;
                  return (
                    <div
                      key={o.id}
                      data-testid={`my-order-${o.id}`}
                      className="bg-white border border-stone-200 rounded-sm p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-center"
                    >
                      <div className="md:col-span-3">
                        <div className="font-mono text-xs text-stone-500">#{o.tracking_code}</div>
                        <div className="text-xs text-stone-500 mt-1">{new Date(o.created_at).toLocaleDateString()}</div>
                        {o.preferred_delivery_date && (
                          <div className="text-xs text-[#b45f06] font-semibold mt-1">
                            Deliver: {o.preferred_delivery_date}
                          </div>
                        )}
                      </div>
                      <div className="md:col-span-4 text-sm text-stone-700">
                        {o.items.slice(0, 2).map((it, i) => (
                          <div key={i}>{it.name_en} — {it.weight_kg}kg × {it.quantity}</div>
                        ))}
                        {o.items.length > 2 && <div className="text-xs text-stone-500">+ {o.items.length - 2} more</div>}
                      </div>
                      <div className="md:col-span-2 text-right md:text-left">
                        <div className="font-bold text-stone-900">₹{o.total.toFixed(0)}</div>
                        <div className={`text-[10px] uppercase tracking-wider font-semibold ${o.payment_status === "paid" ? "text-green-700" : "text-amber-700"}`}>
                          {o.payment_status}
                        </div>
                      </div>
                      <div className="md:col-span-2">
                        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-sm ${STATUS_COLORS[o.status]}`}>
                          <Icon size={11} /> {o.status.replace("_", " ")}
                        </span>
                      </div>
                      <div className="md:col-span-1 text-right flex md:block gap-2 justify-end">
                        <Link
                          to={`/track/${o.tracking_code}`}
                          data-testid={`my-order-track-${o.id}`}
                          className="text-xs text-[#274e13] font-semibold hover:underline block"
                        >
                          Track
                        </Link>
                        <button
                          data-testid={`my-order-pdf-${o.id}`}
                          onClick={() => downloadInvoice(o.id, o.tracking_code)}
                          className="text-xs text-stone-700 hover:text-[#274e13] inline-flex items-center gap-1 mt-1"
                        >
                          <Download size={11} /> PDF
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {tab === "prebook" && (
          <PreBookForm
            products={products}
            customer={customer}
            onPlaced={() => {
              reload();
              setTab("orders");
              toast.success(lang === "en" ? "Pre-booking placed!" : "ముందస్తు బుకింగ్ పూర్తయింది!");
            }}
          />
        )}
      </div>
      {reviewOrder && (
        <ReviewModal
          order={reviewOrder}
          onClose={() => setReviewOrder(null)}
          onSubmitted={() => { setReviewOrder(null); toast.success("Review submitted"); }}
        />
      )}
    </div>
  );
}

function ReviewModal({ order, onClose, onSubmitted }) {
  const [productId, setProductId] = useState(order.items[0]?.product_id || "");
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await customerApi.post("/reviews", { product_id: productId, rating: Number(rating), comment });
      onSubmitted();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4">
      <form onSubmit={submit} className="bg-white rounded-sm w-full max-w-md p-6" data-testid="review-modal">
        <h2 className="text-xl font-semibold mb-4">Leave a review</h2>
        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Product</label>
            <select data-testid="review-product" value={productId} onChange={(e) => setProductId(e.target.value)} className="mt-1 w-full bg-[#fafaf9] border border-stone-300 rounded-sm px-3 py-2 text-sm">
              {order.items.map((it) => (
                <option key={it.product_id} value={it.product_id}>{it.name_en}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Rating</label>
            <div className="mt-1 flex gap-1 text-2xl">
              {[1, 2, 3, 4, 5].map((r) => (
                <button type="button" key={r} data-testid={`review-star-${r}`} onClick={() => setRating(r)} className={r <= rating ? "text-amber-500" : "text-stone-300"}>★</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Comment (optional)</label>
            <textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={3} className="mt-1 w-full bg-[#fafaf9] border border-stone-300 rounded-sm px-3 py-2 text-sm" />
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-stone-300 rounded-sm">Cancel</button>
          <button type="submit" disabled={saving} data-testid="review-submit" className="px-4 py-2 text-sm bg-[#274e13] text-white rounded-sm font-semibold">{saving ? "Submitting..." : "Submit review"}</button>
        </div>
      </form>
    </div>
  );
}

function PreBookForm({ products, customer, onPlaced }) {
  const [selections, setSelections] = useState({}); // {product_id: {weight, qty}}
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const setSel = (id, patch) =>
    setSelections((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));

  const cartItems = products
    .map((p) => {
      const s = selections[p.id];
      if (!s || !s.qty || s.qty <= 0) return null;
      return {
        product_id: p.id,
        name_en: p.name_en,
        name_te: p.name_te,
        weight_kg: s.weight || (p.pack_sizes?.[0] || 5),
        price_per_kg: p.price_per_kg,
        quantity: s.qty,
        subtotal: (s.weight || (p.pack_sizes?.[0] || 5)) * p.price_per_kg * s.qty,
      };
    })
    .filter(Boolean);

  const total = cartItems.reduce((sum, it) => sum + it.subtotal, 0);

  const submit = async () => {
    if (cartItems.length === 0) {
      toast.error("Add at least one product");
      return;
    }
    if (!date) {
      toast.error("Pick a preferred delivery date");
      return;
    }
    setSubmitting(true);
    try {
      await customerApi.post("/orders", {
        customer_name: customer.business_name,
        customer_phone: customer.phone,
        customer_address: customer.address,
        notes,
        items: cartItems,
        payment_method: "whatsapp",
        customer_id: customer.id,
        preferred_delivery_date: date,
      });
      onPlaced();
      setSelections({});
      setDate("");
      setNotes("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to place order");
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="mt-8 space-y-6">
      <div className="bg-white border border-stone-200 rounded-sm p-6">
        <div className="text-sm font-semibold text-stone-900 mb-1">Preferred delivery date</div>
        <input
          data-testid="prebook-date"
          type="date"
          min={today}
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="bg-[#fafaf9] border border-stone-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#274e13]"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map((p) => {
          const sizes = p.pack_sizes && p.pack_sizes.length ? p.pack_sizes : [5, 10, 26, 50];
          const sel = selections[p.id] || { weight: sizes[0], qty: 0 };
          return (
            <div key={p.id} className="bg-white border border-stone-200 rounded-sm p-4">
              <div className="font-semibold text-stone-900">{p.name_en}</div>
              <div className="text-xs text-stone-500 font-telugu">{p.name_te}</div>
              <div className="text-sm font-semibold text-[#274e13] mt-2">₹{p.price_per_kg.toFixed(0)} / kg</div>
              <div className="mt-3 flex gap-1 flex-wrap">
                {sizes.map((w) => (
                  <button
                    key={w}
                    data-testid={`prebook-weight-${p.id}-${w}`}
                    onClick={() => setSel(p.id, { weight: w })}
                    className={`text-xs px-2 py-1 rounded-sm border ${
                      sel.weight === w ? "bg-[#274e13] text-white border-[#274e13]" : "border-stone-300"
                    }`}
                  >
                    {w}kg
                  </button>
                ))}
              </div>
              <div className="mt-3 flex items-center justify-between">
                <div className="flex items-center border border-stone-300 rounded-sm">
                  <button data-testid={`prebook-dec-${p.id}`} onClick={() => setSel(p.id, { qty: Math.max(0, sel.qty - 1) })} className="p-1.5"><Minus size={14} /></button>
                  <span className="px-3 text-sm font-semibold">{sel.qty || 0}</span>
                  <button data-testid={`prebook-inc-${p.id}`} onClick={() => setSel(p.id, { qty: (sel.qty || 0) + 1 })} className="p-1.5"><Plus size={14} /></button>
                </div>
                <div className="text-sm font-semibold">
                  ₹{((sel.weight || sizes[0]) * p.price_per_kg * (sel.qty || 0)).toFixed(0)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-[#f2ebd9] border border-stone-200 rounded-sm p-6">
        <div className="text-sm font-semibold text-stone-900 mb-2">Notes / special instructions</div>
        <textarea
          data-testid="prebook-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="w-full bg-white border border-stone-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#274e13]"
        />
        <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Total</div>
            <div className="text-3xl font-bold text-[#274e13]">₹{total.toFixed(0)}</div>
          </div>
          <button
            data-testid="prebook-submit"
            onClick={submit}
            disabled={submitting}
            className="bg-[#274e13] hover:bg-[#38761d] text-white px-8 py-3 rounded-sm font-semibold tracking-wide disabled:opacity-60"
          >
            {submitting ? "Submitting..." : "Place pre-booking"}
          </button>
        </div>
      </div>
    </div>
  );
}
